import captureUrl from "./capture.worklet.ts?worker&url";
import playbackUrl from "./playback.worklet.ts?worker&url";
import type { VoiceFrame } from "./frames";

// Browser audio for team voice: one AudioContext, a playback worklet wired to
// the speakers, and (only while you are in voice) the mic through the capture
// worklet, with echo cancellation, noise suppression and auto gain on. The
// context is created on the first user gesture because browsers refuse to
// start audio before one.

export type MicResult = "ok" | "denied" | "unsupported" | "busy" | "error";

export class VoiceAudio {
  private ctx: AudioContext | null = null;
  private ready: Promise<void> | null = null;
  private playback: AudioWorkletNode | null = null;
  private mic: { stream: MediaStream; src: MediaStreamAudioSourceNode; node: AudioWorkletNode; sink: GainNode } | null = null;
  private micPending: Promise<MicResult> | null = null;
  private listeners = new Set<() => void>();
  onFrame: ((pcm: Int16Array) => void) | null = null;

  /** True once audio can actually play (context running). */
  get running(): boolean {
    return this.ctx?.state === "running";
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }
  private emit() {
    this.listeners.forEach((l) => l());
  }

  /** Create (first time) and resume the context. Call from a user gesture. */
  unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return Promise.resolve();
      const ctx = new Ctor({ latencyHint: "interactive" });
      this.ctx = ctx;
      ctx.addEventListener("statechange", () => this.emit());
      this.ready = (async () => {
        await ctx.audioWorklet.addModule(playbackUrl);
        await ctx.audioWorklet.addModule(captureUrl);
        const node = new AudioWorkletNode(ctx, "br-playback", { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [1] });
        node.connect(ctx.destination);
        this.playback = node;
      })();
    }
    const ctx = this.ctx;
    const resume = ctx.state === "suspended" ? ctx.resume().catch(() => undefined) : Promise.resolve();
    return Promise.all([resume, this.ready]).then(() => this.emit());
  }

  play(frame: VoiceFrame): void {
    const node = this.playback;
    if (!node || this.ctx?.state !== "running") return;
    const pcm = frame.pcm.slice();
    node.port.postMessage({ type: "frame", key: frame.userId, pcm: pcm.buffer }, [pcm.buffer]);
  }

  /** Open the mic (asks permission the first time). Safe to call repeatedly. */
  async openMic(): Promise<MicResult> {
    if (this.mic) return "ok";
    if (this.micPending) return this.micPending;
    this.micPending = (async (): Promise<MicResult> => {
      if (!navigator.mediaDevices?.getUserMedia) return "unsupported";
      await this.unlock();
      const ctx = this.ctx;
      if (!ctx) return "unsupported";
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 },
        });
      } catch (err) {
        const name = err instanceof DOMException ? err.name : "";
        if (name === "NotAllowedError" || name === "SecurityError") return "denied";
        if (name === "NotReadableError" || name === "AbortError") return "busy";
        if (name === "NotFoundError" || name === "OverconstrainedError") return "unsupported";
        return "error";
      }
      await this.ready;
      const src = ctx.createMediaStreamSource(stream);
      const node = new AudioWorkletNode(ctx, "br-capture", { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1], channelCount: 1, channelCountMode: "explicit" });
      node.port.onmessage = (e: MessageEvent<ArrayBuffer>) => this.onFrame?.(new Int16Array(e.data));
      // A silent sink keeps the capture node pulled in every browser.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      src.connect(node);
      node.connect(sink);
      sink.connect(ctx.destination);
      this.mic = { stream, src, node, sink };
      stream.getAudioTracks()[0]?.addEventListener("ended", () => this.closeMic());
      this.emit();
      return "ok";
    })().finally(() => {
      this.micPending = null;
    });
    return this.micPending;
  }

  get micOpen(): boolean {
    return this.mic !== null;
  }

  /** Stop capturing now (leaving voice), so the browser's recording light goes off. */
  closeMic(): void {
    const m = this.mic;
    if (!m) return;
    this.mic = null;
    m.node.port.onmessage = null;
    m.src.disconnect();
    m.node.disconnect();
    m.sink.disconnect();
    m.stream.getTracks().forEach((t) => t.stop());
    this.emit();
  }

  dispose(): void {
    this.closeMic();
    this.playback?.disconnect();
    this.playback = null;
    void this.ctx?.close().catch(() => undefined);
    this.ctx = null;
    this.ready = null;
  }
}
